import { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { type Provider } from '@renderer/hooks/use-providers'
import { Logo } from '@highlight/ui'
import { providerLogo } from '@renderer/utils/provider-logo'

// Providers with a bespoke brand glyph render it instead of the catalog `iconUrl` bitmap.
const BESPOKE_GLYPH_PROVIDERS = new Set(['slack', 'linear', 'notion'])

// Black-on-transparent marks that vanish on dark surfaces.
const DARK_INVERTED_PROVIDER_ICONS = new Set(['juicebox'])

/** Classes for a provider's catalog `iconUrl` bitmap, inverted in dark mode when the mark would otherwise vanish. */
export function providerIconClassName(providerId: string, className?: string): string {
  return twMerge(className, DARK_INVERTED_PROVIDER_ICONS.has(providerId) && 'dark:invert')
}

/** Brand icon for an integration provider, sized in pixels (default 24). */
export function ProviderIcon({ provider, size = 24 }: { provider: Provider; size?: number }): ReactNode {
  const logo = BESPOKE_GLYPH_PROVIDERS.has(provider.id) ? providerLogo(provider.id) : undefined
  if (logo) return <Logo name={logo} size={size} />
  // Round square CDN marks (Ashby, etc.) to match the bespoke brand glyphs.
  return (
    <img
      src={provider.iconUrl}
      alt={provider.name}
      width={size}
      height={size}
      className={providerIconClassName(provider.id, 'rounded-md object-contain')}
    />
  )
}
